"use client";

import React from "react";
import type { Deal } from "@/lib/types/database/deals";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import { useDealWithLinkedCompanyFromDetail } from "@/contexts/DetailContextProvider";
import { DealCommunications } from "../DealCommunications";
import { CompanyQuickBooksPanel } from "@/features/companies/components/CompanyQuickBooksPanel";

export function DealCommunicationsDisplay({ item }: TabContentProps<Deal>) {
  const fromCtx = useDealWithLinkedCompanyFromDetail();
  const companyId =
    fromCtx?.linkedCompany.id ??
    (typeof item.company_id === "string" && item.company_id.length > 0 ? item.company_id : "");
  // `deals.company_name` is a nullable denormalized column — `?? undefined` so
  // a NULL reads as "not known" rather than being passed down as `null` (#2633).
  const companyDisplayName =
    fromCtx?.linkedCompany.displayName ?? item.company_name ?? undefined;

  if (!companyId) {
    return (
      <div className="rounded-lg border p-4 text-sm text-gray-600 dark:text-gray-400">
        This deal is not linked to a company yet. Link a company on the Deal or Company tab to view
        communications.
      </div>
    );
  }

  const dealId = fromCtx?.dealId ?? item.id;

  return (
    <DealCommunications
      dealId={dealId}
      companyId={companyId}
      companyDisplayName={companyDisplayName}
      className="min-h-[200px]"
    />
  );
}

export function DealQuickBooksDisplay({ item }: TabContentProps<Deal>) {
  const fromCtx = useDealWithLinkedCompanyFromDetail();
  const companyId =
    fromCtx?.linkedCompany.id ??
    (typeof item.company_id === "string" && item.company_id.length > 0 ? item.company_id : null);
  const displayName =
    fromCtx?.linkedCompany.displayName ??
    (typeof item.company_name === "string" && item.company_name.length > 0
      ? item.company_name
      : "");

  if (!companyId) {
    return (
      <div className="rounded-lg border p-4 text-sm text-gray-600 dark:text-gray-400">
        This deal is not linked to a company yet. Link a CRM company on the Deal or Company tab to
        view QuickBooks connections for that account.
      </div>
    );
  }

  return (
    <CompanyQuickBooksPanel
      companyId={companyId}
      companyDisplayName={displayName.length > 0 ? displayName : "Company"}
    />
  );
}
